/**
 * Turvalisus → MFA + passkey API-wrapperid. Peegeldab aishopi
 * `web_client/lib/auth.ts` + `account/sections/security.tsx` mustreid.
 */
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";
import { api } from "./api";
import type { AccountUser, LoginResult } from "./auth";

export interface MfaFactor {
  id: string;
  type: string;
  label: string | null;
  isPrimary: boolean;
  verifiedAt: string | null;
  enabledAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface TotpEnrollment {
  factorId: string;
  secret: string;
  otpauthUrl: string;
  issuer: string;
}

export interface MfaChallengeInfo {
  methods: { totp: boolean; email_otp: boolean; backup_code: boolean };
}

export const mfaApi = {
  listFactors: () => api.get<{ factors: MfaFactor[] }>("/auth/mfa/factors"),
  totpBegin: (label?: string) =>
    api.post<TotpEnrollment>("/auth/mfa/totp/enroll/begin", label ? { label } : {}),
  totpVerify: (factorId: string, code: string) =>
    api.post<{ factor: MfaFactor; backupCodes?: string[] }>("/auth/mfa/totp/enroll/verify", { factorId, code }),
  emailBegin: () => api.post<{ factorId: string; sentTo: string }>("/auth/mfa/email/enroll/begin", {}),
  emailVerify: (factorId: string, code: string) =>
    api.post<{ factor: MfaFactor; backupCodes?: string[] }>("/auth/mfa/email/enroll/verify", { factorId, code }),
  emailSendLogin: (pendingToken: string) =>
    api.post<{ ok: true; sentTo?: string }>("/auth/mfa/email/send", { pendingToken }),
  challengeInfo: (pendingToken: string) =>
    api.post<MfaChallengeInfo>("/auth/mfa/challenge-info", { pendingToken }),
  removeFactor: (id: string) => api.del(`/auth/mfa/factors/${id}`),
  backupCodesStatus: () => api.get<{ total: number; unused: number }>("/auth/mfa/backup-codes/status"),
  regenerateBackup: () => api.post<{ codes: string[] }>("/auth/mfa/backup-codes/regenerate", {}),
  /** Login-aegne teine faktor → tagastab täis-kasutaja (buildMe). */
  verify: (pendingToken: string, code: string) =>
    api.post<AccountUser>("/auth/mfa/verify", { pendingToken, code }),
};

// ── Passkey'd (WebAuthn) ──────────────────────────────────────────────────────
export interface Passkey {
  id: string;
  deviceName: string | null;
  aaguid: string | null;
  backupEligible: boolean;
  backupState: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

/** Klass kasutaja-loobumiste eristamiseks päris vigadest. */
export class WebAuthnUnsupportedError extends Error {}

/**
 * Passkey = platvormi-autentija (Touch ID / Face ID / Windows Hello /
 * Androidi sõrmejälg) ehk biomeetriline volitus. Vajab turvalist konteksti
 * (HTTPS või localhost) + brauseri-tuge.
 */
export function isWebAuthnReady(): boolean {
  return typeof window !== "undefined" && window.isSecureContext && browserSupportsWebAuthn();
}

/** WebAuthn loobumine/timeout - pole päris viga, ei näita kasutajale veateadet. */
export function isWebAuthnCancel(err: unknown): boolean {
  return err instanceof Error && (err.name === "NotAllowedError" || err.name === "AbortError");
}

/** Inimsõbralik seadme-nimi praegusest brauserist (passkey-vaikenimi). */
function deviceLabel(): string {
  const ua = navigator.userAgent;
  if (/Mobile|Android|iPhone|iPad/.test(ua)) return "Mobiil";
  if (/Mac/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  return "Brauser";
}

export const passkeyApi = {
  list: () => api.get<{ passkeys: Passkey[] }>("/auth/passkey/list"),
  remove: (id: string) => api.del(`/auth/passkey/${id}`),

  /** Registreeri uus passkey (vajab sisselogitud sessiooni). */
  async register(): Promise<void> {
    const options = await api.post<Parameters<typeof startRegistration>[0]>(
      "/auth/passkey/register/begin",
      {},
    );
    const response = await startRegistration(options);
    await api.post("/auth/passkey/register/complete", { response, deviceName: deviceLabel() });
  },

  /**
   * Passkey-/biomeetriline sisselogimine. Valikulise e-postiga saadab server
   * `allowCredentials`-i (brauser läheb otse Touch ID-le); ilma e-postita
   * kasutab discoverable-credential'eid. Tagastab sessiooni VÕI MFA-värava.
   */
  async login(email?: string): Promise<LoginResult> {
    if (!isWebAuthnReady()) throw new WebAuthnUnsupportedError("webauthn_unsupported");
    const options = await api.post<Parameters<typeof startAuthentication>[0]>(
      "/auth/passkey/login/begin",
      email ? { email } : {},
    );
    const response = await startAuthentication(options);
    return api.post<LoginResult>("/auth/passkey/login/complete", { response });
  },
};
